/**
 * Risk profile result screen — display only.
 *
 * Belongs here: ready UI from session riskProfile; hub vs journey chrome; missing-profile fallback UI.
 * Does not belong here: submit, compute, loader, or Find-my-strategy (see StrategyResolveOverlay).
 */
import { useEffect } from 'react'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { EventNames } from '../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'
import { expertReviewSafeAreaTopStyle } from '../../shared/expertReviewSafeArea'
import { useStrategyQuestionnaireContext } from '../flow-engine/StrategyQuestionnaireProvider'
import { getAllocationRiskProfileConfig } from '../setup/config'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { useExpertReviewNavigation } from '../../navigation/expertReviewNavigation'
import { EXPERT_REVIEW_CAME_FROM } from '../../navigation/expertReviewNavigation'
import { useExpertReviewStatusContext } from '../../providers/ExpertReviewStatusContext'
import { isQuestionnaireStatusDone } from '../../shared/types/questionnaire.types'
import { ExpertReviewTopBar } from '../../shared/components/ExpertReviewTopBar'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../../analytics/expertReviewEvents'
import type { AllocationRiskProfile } from '../../types/allocation.types'

const RISK_PROFILE_BG_PATTERN_SRC = '/images/allocation/risk-profiles-v2/bg-pattern.svg'
const RISK_PROFILE_ERROR_ASSET_SRC = '/images/mf-l2/alert-triangle.svg'

export default function AllocationRiskProfilePage() {
  const navigation = useExpertReviewNavigation()
  const { statusData } = useExpertReviewStatusContext()
  const { riskProfile } = useStrategyQuestionnaireContext()
  const openedFromHub =
    navigation.navigationState.cameFrom === EXPERT_REVIEW_CAME_FROM.HUB

  if (riskProfile == null) {
    return (
      <RiskProfileMissingPage
        onBack={() => navigation.goBackFromRiskProfile()}
      />
    )
  }

  function handleBack() {
    navigation.goBackFromRiskProfile()
  }

  function handleReviewInputs() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: ExpertReviewScreen.RiskProfile,
        eventId: ExpertReviewEventIds.RiskReviewInputs,
        buttonLabel: 'Review inputs'
      }
    })
    // Replace so back pops to hub; RISK_PROFILE cameFrom hides CTA until answers change
    navigation.goToStrategyReview({
      cameFrom: EXPERT_REVIEW_CAME_FROM.RISK_PROFILE,
      replace: true
    })
  }

  function handleContinue() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: ExpertReviewScreen.RiskProfile,
        eventId: ExpertReviewEventIds.RiskContinue,
        buttonLabel: 'Continue'
      }
    })
    if (isQuestionnaireStatusDone(statusData?.preferenceQuestionnaire?.status)) {
      navigation.goToHub({ replace: true })
      return
    }
    navigation.goToPreferencesList({ cameFrom: EXPERT_REVIEW_CAME_FROM.RISK_PROFILE })
  }

  return (
    <RiskProfileReadyPage
      profile={riskProfile}
      openedFromHub={openedFromHub}
      onBack={handleBack}
      onReviewInputs={handleReviewInputs}
      onContinue={handleContinue}
    />
  )
}

/** Profile found — hero, copy, hub vs continue CTA. */
export function RiskProfileReadyPage({
  profile,
  openedFromHub,
  onBack,
  onReviewInputs,
  onContinue
}: {
  profile: AllocationRiskProfile
  openedFromHub: boolean
  onBack: () => void
  onReviewInputs: () => void
  onContinue: () => void
}) {
  const config = getAllocationRiskProfileConfig(profile)

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.RiskProfile }
    })
  }, [])

  return (
    <div
      className="relative flex h-screen flex-col min-h-0 overflow-hidden animate-fade-250"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`,
        background: 'linear-gradient(180deg, #EDCEC4 0%, #FFF 100%)'
      }}
    >
      {/* Soft-light must be CSS on <img> — SVG-as-image can't blend with the page gradient */}
      {/* Width scales with viewport height: SE (~667) stays full; taller phones grow */}
      <img
        src={RISK_PROFILE_BG_PATTERN_SRC}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 z-0 h-auto w-full max-w-none -translate-x-1/2 object-cover opacity-40 
        [@media(min-height:680px)]:w-[140%] [@media(min-height:780px)]:w-[180%]"
      />

      <div className="relative z-10 shrink-0 px-5">
        <ExpertReviewTopBar
          onBack={onBack}
          menu="strategy"
          analyticsScreen={ExpertReviewScreen.RiskProfile}
          testLocatorBack="strategy-risk-profile-back"
          testLocatorMenu="strategy-risk-profile-menu"
        />
      </div>

      <div className="relative z-10 flex flex-1 min-h-0 flex-col items-center overflow-auto no-scrollbar px-5">
        <div className="my-auto flex w-full max-w-[303px] flex-col items-center gap-9">
          {/* Overlay/hard-light live inside the profile SVG strokes — not on this wrapper */}
          <div className="flex h-[164px] w-[164px] items-center justify-center mix-blend-soft-light" aria-hidden>
            <img src={config.assetSrc} alt="" className="h-[164px] w-[164px] object-contain" />
          </div>

          <div className="flex w-full flex-col items-center gap-4 text-center">
            <div className="flex flex-col items-center">
              <p
                className="text-[14px] font-[400] leading-[140%] text-[#232323] opacity-60"
                test-locator="strategy-risk-profile-subtitle"
              >
                Recommended allocation strategy
              </p>
              <h1
                className="mt-[-4px] bg-clip-text text-[32px] font-[500] leading-[140%] text-transparent opacity-80"
                style={{
                  backgroundImage:
                    'linear-gradient(93deg, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.9) 100%)'
                }}
                test-locator="strategy-risk-profile-title"
              >
                {config.name}
              </h1>
            </div>

            <div
              className="text-[14px] leading-[150%] text-[rgba(0,0,0,0.7)]"
              test-locator="strategy-risk-profile-description"
            >
              <RenderHTML innerHTMLText={config.description} />
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 shrink-0 flex items-end pt-1 w-full px-5">
        <PrimaryButton
          testLocator={
            openedFromHub
              ? 'strategy-risk-profile-review-inputs'
              : 'strategy-risk-profile-continue'
          }
          onClick={openedFromHub ? onReviewInputs : onContinue}
          label={openedFromHub ? 'Review inputs' : 'Continue'}
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px] !bg-[#232323]"
        />
      </div>
    </div>
  )
}

/** Session has no profile (refresh / deep link) — same error chrome as compute failure, back only. */
export function RiskProfileMissingPage({
  onBack
}: {
  onBack: () => void
}) {
  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.RiskProfile }
    })
  }, [])

  return (
    <div
      className="relative flex h-screen flex-col min-h-0 overflow-hidden animate-fade-250"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`,
        background: 'linear-gradient(180deg, #FFB4A8 0%, #FFF 100%)'
      }}
      role="alert"
    >
      <img
        src={RISK_PROFILE_BG_PATTERN_SRC}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 z-0 h-auto w-full max-w-none -translate-x-1/2 object-cover opacity-40
        [@media(min-height:680px)]:w-[140%] [@media(min-height:780px)]:w-[180%]"
      />

      <div className="relative z-10 shrink-0 px-5">
        <ExpertReviewTopBar
          onBack={onBack}
          menu="strategy"
          analyticsScreen={ExpertReviewScreen.RiskProfile}
          testLocatorBack="strategy-risk-profile-missing-back"
          testLocatorMenu="strategy-risk-profile-missing-menu"
        />
      </div>

      <div className="relative z-10 flex flex-1 min-h-0 flex-col items-center overflow-auto no-scrollbar px-5">
        <div className="my-auto flex w-full max-w-[303px] flex-col items-center gap-10">
          <div className="flex h-[124px] w-[124px] items-center justify-center" aria-hidden>
            <img
              src={RISK_PROFILE_ERROR_ASSET_SRC}
              alt=""
              className="h-[88px] w-[88px] object-contain"
            />
          </div>

          <div className="flex w-full flex-col items-center gap-3 text-center">
            <h1
              className="text-[28px] font-[500] leading-[130%] text-[#232323]"
              test-locator="strategy-risk-profile-missing-title"
            >
              Something went wrong
            </h1>
            <p
              className="text-[14px] font-[400] leading-[150%] text-[rgba(0,0,0,0.7)]"
              test-locator="strategy-risk-profile-missing-description"
            >
              We couldn’t load your allocation strategy. Go back and try again.
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 shrink-0 flex items-end pt-1 w-full px-5">
        <PrimaryButton
          testLocator="strategy-risk-profile-missing-back-cta"
          onClick={() => {
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: ExpertReviewScreen.RiskProfile,
                eventId: ExpertReviewEventIds.RiskGoBack,
                buttonLabel: 'Go back'
              }
            })
            onBack()
          }}
          label="Go back"
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px] !bg-[#232323]"
        />
      </div>
    </div>
  )
}
